import { type JSX, useRef, useState } from 'react';

import type {
	actionComponent,
	actionType,
	ICallSection,
	IrightPanelCTA,
	IRightSection,
	IRightSectionSnapshot,
	//   IRightSectionHistory,
	IRSAction,
} from '../components/reusable/right-panel/utils';
import type { ResultType } from '../constants/global';

export const useRightPanel = <K extends {}>(): IRightSection<K> => {
	const [title, setTitle] = useState<string | JSX.Element>('');
	const [resultType, setResultType] = useState<ResultType | null>(null);
	const [dataRequestCounter, setDataRequestCounter] = useState<number>(0);
	const [openSection, setOpenSection] = useState<boolean>(() => false);
	const [slug, setSlug] = useState<string>(() => '');
	const [cta, setCTA] = useState<IrightPanelCTA[]>([]);
	const [max, setMax] = useState<boolean>(false);
	const [compact, setCompact] = useState<boolean>(false);
	const [half, setHalf] = useState<boolean>(false);
	const [quiet, setQuiet] = useState<boolean>(false);
	const [breadcrumb, setBreadcrumb] = useState<string[]>([]);
	const [action, setAction] = useState<IRSAction>({
		type: null,
		component: '',
		id: null,
	});
	const [data, setData] = useState<{
		onRefresh?: () => void;
		[key: string]: any;
	} | null>(null);

	function updateData(data: K | null) {
		setData(data);
	}

	const isView = (type: actionType[], component: actionComponent) => type.includes(action.type) && action.component === component;

	function callSection<T>(arg: ICallSection<T>) {
		const { action, component, title, id, data, slug, cta, max, compact, half, quiet, resultType, breadcrumb } = arg;
		setAction((prev) => ({
			type: action || prev.type,
			component: component || prev.component,
			id: id || prev.id,
		}));
		setTitle((prev) => title || prev);
		setOpenSection(true);
		setData(data as unknown as K);
		setSlug((prev) => slug || prev);
		setCTA((prev) => cta || prev);
		setMax((prev) => max || prev);
		setCompact(compact ?? false);
		setHalf(half ?? false);
		// `?? false`, not `|| prev` — a panel that did not ask for a quiet backdrop must
		// not inherit one from whatever was open before it.
		setQuiet(quiet ?? false);
		setResultType((prev) => resultType || prev);
		setBreadcrumb(breadcrumb ?? []);
		setCTA([]);
	}

	const isRestoringRef = useRef(false);

	/**
	 * Veto for CHROME-driven dismissal — Escape, the backdrop and the X button, all of
	 * which route through the shell's `closeRightSection`. A ref, not state: the guard
	 * is read only at close time and registering one must not re-render the shell.
	 * Direct `closeSection()` calls (a panel closing itself after a successful submit —
	 * ~55 call sites) bypass it BY DESIGN, as does panel-history back/forward.
	 */
	const beforeCloseRef = useRef<(() => boolean | Promise<boolean>) | null>(null);
	const setBeforeClose = (guard: (() => boolean | Promise<boolean>) | null) => {
		beforeCloseRef.current = guard;
	};

	/**
	 * Re-apply a panel-history snapshot to the live state (back/forward navigation).
	 * Panels read `data`/`title`/`breadcrumb`/… from this context, so restoring the
	 * component alone is not enough — the full snapshot must be put back, otherwise
	 * the remounted parent panel sees the newest (child) panel's data.
	 */
	function restoreSection(snap: IRightSectionSnapshot) {
		isRestoringRef.current = true; // consumed by the history push effect in GlobalRightSection
		setAction(snap.action);
		setTitle(snap.title);
		setData(snap.data);
		setSlug(snap.slug);
		setCTA(snap.cta ?? []);
		setMax(snap.max);
		setCompact(snap.compact);
		setHalf(snap.half);
		setQuiet(snap.quiet);
		setResultType(snap.resultType);
		setBreadcrumb(snap.breadcrumb ?? []);
		// openSection stays true — restores only happen while the panel is open
	}

	const closeSection = () => {
		// Same rule as `quiet` in callSection: the next panel must not inherit a stale
		// guard closed over this panel's unmounted state.
		beforeCloseRef.current = null;
		setAction({ component: '', type: null, id: null });
		setOpenSection(false);
		setSlug('');
		setCTA([]);
		setMax(false);
		setCompact(false);
		setHalf(false);
		setQuiet(false);
		setResultType(null);
		setBreadcrumb([]);
	};

	return {
		closeSection,
		openSection,
		setAction,
		action,
		setTitle,
		title,
		callSection,
		isView,
		data,
		updateData,
		setDataRequestCounter,
		dataRequestCounter,
		slug,
		cta,
		max,
		compact,
		setCompact,
		half,
		setHalf,
		quiet,
		resultType,
		setMax,
		setCTA,
		breadcrumb,
		restoreSection,
		isRestoringRef,
		beforeCloseRef,
		setBeforeClose,
	};
};
